import React, { forwardRef, useEffect } from 'react';
import {
    ICallChecklistAuditSimilarityResource,
    IAuditSimilarityColMapped,
} from '../../../../resources/ICallChecklistAuditSimilarity.resource';
import ClickCopyBtn from '../../../AutomaticAssessmentPage/components/ClickCopyBtn';
import TdDate from '../../../AutomaticAssessmentPage/components/TdDate';
import HeadFilter, { EnumSortType } from '../../../AutomaticAssessmentPage/components/HeadFilter';
import Tooltip from '../../../../components/Tooltip';
import StringService from '../../../../services/StringService';
import ReasoningButton from '../../../../components/ReasoningButton';
import styles from './styles.module.scss';
import { NavLink } from 'react-router-dom';

const LinkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path fillRule="evenodd" clipRule="evenodd"
              d="M2 8C2 7.72386 2.22386 7.5 2.5 7.5H4.5C4.77614 7.5 5 7.72386 5 8C5 8.27614 4.77614 8.5 4.5 8.5H2.5C2.22386 8.5 2 8.27614 2 8Z"
              fill="#1E1E1E"/>
        <path fillRule="evenodd" clipRule="evenodd"
              d="M6.5 8C6.5 7.72386 6.72386 7.5 7 7.5H13.5C13.7761 7.5 14 7.72386 14 8C14 8.27614 13.7761 8.5 13.5 8.5H7C6.72386 8.5 6.5 8.27614 6.5 8Z"
              fill="#1E1E1E"/>
        <path fillRule="evenodd" clipRule="evenodd"
              d="M10.1464 4.64645C10.3417 4.45118 10.6583 4.45118 10.8536 4.64645L13.8536 7.64645C14.0488 7.84171 14.0488 8.15829 13.8536 8.35355L10.8536 11.3536C10.6583 11.5488 10.3417 11.5488 10.1464 11.3536C9.95118 11.1583 9.95118 10.8417 10.1464 10.6464L12.7929 8L10.1464 5.35355C9.95118 5.15829 9.95118 4.84171 10.1464 4.64645Z"
              fill="#1E1E1E"/>
    </svg>
);

interface IProps {
    data: ICallChecklistAuditSimilarityResource[];
    selectedSort: string;
    handleSelectSort: (value: string) => void;
    filter?: string;
    filterDuration?: string;
    companyId: number;
    checklistId: number;
    columnStats: Record<string, number>;
}

const SCROLL_SESSION_KEY = 'checklistAuditTableScroll';

const STYLE_W140 = { width: 140 } as const;
const STYLE_W110 = { width: 110 } as const;
const STYLE_W100 = { width: 100 } as const;
const STYLE_W100_CENTER = { width: 100, textAlign: 'center' } as const;
const STYLE_W120_CENTER = { width: 120, textAlign: 'center' } as const;
const STYLE_W110_CENTER = { width: 110, textAlign: 'center' } as const;
const STYLE_W50 = { width: 50 } as const;

const ChecklistAuditTable = forwardRef<HTMLDivElement, IProps>(({
    data,
    selectedSort,
    handleSelectSort,
    filter,
    filterDuration,
    companyId,
    checklistId,
    columnStats,
}, ref) => {
    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        sessionStorage.setItem(SCROLL_SESSION_KEY, JSON.stringify(scrollTop));
    };

    useEffect(() => {
        const scroll = sessionStorage.getItem(SCROLL_SESSION_KEY);
        if (!scroll) return;
        if (ref && 'current' in ref && ref.current) {
            ref.current.scrollTo(0, Number(scroll));
        }
    }, [data, ref]);

    const colValueFactory = (value: number | null): string | number => {
        switch (true) {
            case value === null:
                return 'n/a';
            case value === 0:
                return 'Ні';
            case value === 1:
                return 'Так';
            default:
                return value;
        }
    };

    return (
        <div
            className={styles.table__container}
            id="table-container"
            ref={ref}
            onScroll={handleScroll}
        >
            <table className={styles.table} cellSpacing={0} cellPadding={0}>
                <thead className={styles.table__thead}>
                    <tr>
                        <td className={styles.table__cell_first}>ID</td>
                        <td className={styles.table__cell_head}>
                            <div style={STYLE_W140}>
                                <HeadFilter
                                    selectedSort={selectedSort}
                                    handleSelectSort={handleSelectSort}
                                    title="Дата та час"
                                    type={EnumSortType.dateCreate}
                                />
                            </div>
                        </td>
                        <td className={styles.table__cell_head}>
                            <div style={STYLE_W110}>
                                <HeadFilter
                                    selectedSort={selectedSort}
                                    handleSelectSort={handleSelectSort}
                                    title="Тривалість"
                                    filter={filterDuration}
                                    isFilter={false}
                                    type={EnumSortType.analyticsDuration}
                                />
                            </div>
                        </td>
                        <td className={styles.table__cell_head}>Телефон</td>
                        <td className={styles.table__cell_head}>
                            <div style={STYLE_W100}>
                                <HeadFilter
                                    selectedSort={selectedSort}
                                    handleSelectSort={handleSelectSort}
                                    title="Оператор"
                                    type={EnumSortType.analyticsOperator}
                                    filter={filter}
                                    isFilter={false}
                                />
                            </div>
                        </td>
                        <td className={styles.table__cell_head}>
                            <div style={STYLE_W100_CENTER}>Метрики</div>
                        </td>
                        <td className={styles.table__cell_head}>
                            <div style={STYLE_W120_CENTER}>Аудит метрики</div>
                        </td>
                        <td className={styles.table__cell_head}>
                            <div style={STYLE_W100}>
                                <HeadFilter
                                    selectedSort={selectedSort}
                                    handleSelectSort={handleSelectSort}
                                    title="Схожість"
                                    type={EnumSortType.similarityScore}
                                />
                            </div>
                        </td>
                        <td className={styles.table__cell_head}></td>
                        {data[0]?.columns.map((col: IAuditSimilarityColMapped) => {
                            const stat = columnStats[col.columnName];
                            return (
                                <td className={styles.table__cell_head} key={col.columnName}>
                                    <div style={STYLE_W110_CENTER}>
                                        <Tooltip
                                            label={col.data.type ?? col.columnName}
                                            fullText={col.data.question ?? col.columnName}
                                        />
                                        <div className={styles.table__cell_legend}>(авто) / (аудит)</div>
                                        {stat !== undefined && (
                                            <div className={styles.table__cell_stat}>
                                                {Math.round(stat)}%
                                            </div>
                                        )}
                                    </div>
                                </td>
                            );
                        })}
                        <td style={STYLE_W50} className={styles.table__cell_last}></td>
                    </tr>
                </thead>
                <tbody className={styles.table__tbody}>
                    {data.map((item: ICallChecklistAuditSimilarityResource) => (
                        <tr key={`${item.phoneCall.uuid}${item.phoneCall.callTime}`}>
                            <td>
                                <ClickCopyBtn text={item.phoneCall.uuid} />
                            </td>
                            <td>
                                <TdDate date={item.phoneCall.callTime} />
                            </td>
                            <td>
                                <TdDate duration={item.phoneCall.duration} />
                            </td>
                            <td>{item.phoneCall.phoneNumber}</td>
                            <td>
                                {item.phoneCall.employee.fullName.length > 7 ? (
                                    <Tooltip
                                        label={StringService.cutString(item.phoneCall.employee.fullName, 15)}
                                        fullText={item.phoneCall.employee.fullName}
                                    />
                                ) : item.phoneCall.employee.fullName}
                            </td>
                            <td>
                                <div className={styles.table__cell_question}>
                                    {item.numMetricSum} / {item.numMetricTotal}
                                </div>
                            </td>
                            <td>
                                <div className={styles.table__cell_question}>
                                    {item.auditNumMetricSum} / {item.auditNumMetricTotal}
                                </div>
                            </td>
                            <td>
                                <div className={styles.table__cell_question}>
                                    {Math.round(item.similarityScore)}%
                                </div>
                            </td>
                            <td>
                                <div className={styles.table__cell_link}>
                                    <NavLink to={`/call/${item.phoneCall.uuid}`}>
                                        <LinkIcon />
                                    </NavLink>
                                </div>
                            </td>
                            {item.columns.map((col: IAuditSimilarityColMapped) => {
                                const isMismatch = col.data.value !== col.data.auditValue;
                                const cellText = `(${colValueFactory(col.data.value)}) / (${colValueFactory(col.data.auditValue)})`;
                                return (
                                    <td
                                        key={col.columnName}
                                        className={isMismatch ? styles.table__cell_mismatch : undefined}
                                    >
                                        <div className={styles.table__cell_question}>
                                            {cellText.length > 7 ? (
                                                <Tooltip
                                                    label={StringService.cutString(cellText, 15)}
                                                    fullText={cellText}
                                                />
                                            ) : cellText}
                                            {col.data.itemUuid && (
                                                <ReasoningButton
                                                    companyId={companyId}
                                                    phonecallUuid={item.phoneCall.uuid}
                                                    checklistId={checklistId}
                                                    itemUuid={col.data.itemUuid}
                                                    questionLabel={`(${col.data.type}) ${col.data.question}`}
                                                    className={styles.table__reasoningBtn}
                                                    variant={col.data.hasHumanReasoning ? 'exclamation' : 'question'}
                                                />
                                            )}
                                        </div>
                                    </td>
                                );
                            })}
                            <td>
                                <NavLink
                                    to={`/call/${item.phoneCall.uuid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <LinkIcon />
                                </NavLink>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
});

export default ChecklistAuditTable;
